import { useEffect, useState } from "react";
import { Clock, LogOut } from "lucide-react";
import { microcopy, useMicrocopy, type MicrocopyKey } from "../lib/microcopy";
import { Button } from "./button";
import { toast } from "./feedback";
import { notifyDone } from "../lib/save";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./overlays";

const WARN_BEFORE_MS = 2 * 60 * 1000;

/** A toast with one of the design system's own messages (session, drafts), in the document's language. */
export function microcopyToast(key: MicrocopyKey, tone: "info" | "success" | "error" = "info"): void {
  const text = microcopy(key);
  if (tone === "success") notifyDone(text);
  else if (tone === "error") toast.error(text);
  else toast.info(text);
}

function remaining(deadline: string | number | null | undefined): number {
  if (deadline === null || deadline === undefined || deadline === "") return Number.POSITIVE_INFINITY;
  return (typeof deadline === "number" ? deadline : new Date(deadline).getTime()) - Date.now();
}

export function sessionClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Strings of the idle and absolute variants (the apps pass their `auth.session.*` texts; `{time}` is filled in). */
export interface SessionWarningLabels {
  title: string;
  /** With `{time}` where the countdown (idle) or the end time (absolute) goes. */
  body: string;
  stay: string;
  signOut: string;
}

/**
 * Session warnings (security §3.3).
 * - `token` (sessions from before the strict sign-in): two minutes before the token lapses a countdown asks whether to stay signed in.
 * - `idle`: two minutes before the idle limit, "Stay signed in" renews the session (it counts as activity) — `deadline` is the idle limit.
 * - `absolute`: ten minutes before the 10-hour limit a non-blocking notice says when the session ends; it cannot be extended.
 * At zero the app's `onExpired` signs the user out (drafts kept by `useDraft` survive and come back after the next sign-in).
 */
export function SessionExpiryWarning({
  expiresAt,
  deadline,
  variant = "token",
  labels,
  endsAtText,
  onExtend,
  onSignOut,
  onExpired,
}: {
  /** Token variant: the token's expiry. */
  expiresAt?: string | null | undefined;
  /** Idle and absolute variants: the limit (ISO or ms epoch). */
  deadline?: string | number | null;
  variant?: "token" | "idle" | "absolute";
  labels?: SessionWarningLabels;
  /** Absolute variant: the end time as the person reads it ("18:40 GST"). */
  endsAtText?: string;
  onExtend?: () => Promise<void>;
  onSignOut: () => void;
  onExpired?: () => void;
}) {
  const m = useMicrocopy();
  const limit = variant === "token" ? expiresAt : deadline;
  const [left, setLeft] = useState(() => remaining(limit));
  const [busy, setBusy] = useState(false);
  const [dismissedPhase, setDismissedPhase] = useState(0);

  useEffect(() => {
    setLeft(remaining(limit));
    if (limit === null || limit === undefined) return;
    const timer = window.setInterval(() => setLeft(remaining(limit)), 1000);
    return () => window.clearInterval(timer);
  }, [limit]);

  useEffect(() => {
    if (limit !== null && limit !== undefined && left <= 0) onExpired?.();
  }, [limit, left, onExpired]);

  if (variant === "absolute") {
    // T − 10 min, and again at T − 2 min: a dismissal holds until the next notice.
    const phase = !Number.isFinite(left) || left <= 0 ? 0 : left <= WARN_BEFORE_MS ? 2 : left <= 10 * 60 * 1000 ? 1 : 0;
    if (phase === 0 || dismissedPhase >= phase || !labels) return null;
    return (
      <div role="status" aria-live="polite" data-session-warning="absolute" className="fixed inset-x-0 top-3 z-[70] mx-auto flex w-fit max-w-[calc(100vw-2rem)] items-center gap-3 rounded-xl border border-warning-200 bg-white px-4 py-2.5 text-sm text-aeblack-900 shadow-lg">
        <Clock className="size-4 shrink-0 text-warning-600" aria-hidden />
        <span className="min-w-0">
          <span className="font-semibold">{labels.title}</span> · {labels.body.replace("{time}", endsAtText ?? "")}
        </span>
        <Button size="sm" variant="ghost" onClick={() => setDismissedPhase(phase)}>{m("close")}</Button>
      </div>
    );
  }

  const open = Number.isFinite(left) && left > 0 && left <= WARN_BEFORE_MS;
  const title = labels?.title ?? m("sessionTitle");
  const body = labels ? labels.body.replace("{time}", sessionClock(left)) : m("sessionBody", { time: sessionClock(left) });

  return (
    <Dialog open={open}>
      <DialogContent size="sm" hideClose role="alertdialog" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-session-warning={variant} data-session-prompt="">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="size-5 text-warning-600" /> {title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <p className="text-center font-heading text-3xl font-semibold tabular text-aeblack-950" aria-live="polite" dir="ltr">{sessionClock(left)}</p>
        <DialogFooter>
          <Button variant="ghost" onClick={onSignOut}><LogOut className="rtl:-scale-x-100" /> {labels?.signOut ?? m("sessionSignOut")}</Button>
          {onExtend ? (
            <Button loading={busy} onClick={async () => { setBusy(true); try { await onExtend(); } finally { setBusy(false); } }} autoFocus>
              {labels?.stay ?? m("sessionExtend")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
